import { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { Card } from '../components/ui/card';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import { countByBand } from '../data/mockData';

const COLORS = {
  healthy: '#10B981',
  watchlist: '#3B82F6',
  maintenance: '#F59E0B',
  critical: '#EF4444',
  offline: '#94A3B8',
};

export default function Analytics() {
  const { devices, alerts, technicians } = useApp();

  const byBand = countByBand(devices);
  const healthPie = Object.entries(byBand).map(([k, v]) => ({
    name: k,
    value: v,
    fill: COLORS[k.toLowerCase()] || '#94A3B8',
  }));

  const alertsByType = useMemo(() => {
    const map = {};
    alerts.forEach((a) => {
      map[a.type] = (map[a.type] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, count]) => ({ name, count }));
  }, [alerts]);

  const alertsBySeverity = [
    { name: 'Critical', count: alerts.filter((a) => a.severity === 'critical').length, fill: COLORS.critical },
    { name: 'Warning', count: alerts.filter((a) => a.severity === 'warning').length, fill: COLORS.maintenance },
    { name: 'Watchlist', count: alerts.filter((a) => a.severity === 'watchlist').length, fill: COLORS.watchlist },
    { name: 'Info', count: alerts.filter((a) => a.severity === 'informational').length, fill: COLORS.offline },
  ];

  const responseTrend = Array.from({ length: 12 }).map((_, i) => ({
    d: `W${i + 1}`,
    hours: +(2.8 - Math.sin(i / 2) * 0.4 - (i > 6 ? 0.3 : 0)).toFixed(1),
  }));

  const seasonalStress = [
    { m: 'Jan', heat: 88, cool: 12 },
    { m: 'Feb', heat: 82, cool: 14 },
    { m: 'Mar', heat: 65, cool: 22 },
    { m: 'Apr', heat: 40, cool: 38 },
    { m: 'May', heat: 18, cool: 62 },
    { m: 'Jun', heat: 8, cool: 86 },
    { m: 'Jul', heat: 5, cool: 98 },
    { m: 'Aug', heat: 6, cool: 96 },
    { m: 'Sep', heat: 14, cool: 74 },
    { m: 'Oct', heat: 38, cool: 42 },
    { m: 'Nov', heat: 70, cool: 20 },
    { m: 'Dec', heat: 85, cool: 10 },
  ];

  const workload = technicians.map((t) => ({ name: t.name.split(' ')[0], jobs: t.activeTickets + t.completedThisMonth }));

  const monitoringRev = [
    { m: 'Jun', rev: 8200 },
    { m: 'Jul', rev: 11400 },
    { m: 'Aug', rev: 12800 },
    { m: 'Sep', rev: 10600 },
    { m: 'Oct', rev: 9800 },
    { m: 'Nov', rev: 11200 },
    { m: 'Dec', rev: 13400 },
    { m: 'Jan', rev: 14800 },
  ];

  const kpis = [
    { label: 'Alerts / Week', value: 58, trend: -12 },
    { label: 'Avg Response', value: '2.3 h', trend: -18 },
    { label: 'Plan Conversion', value: '34%', trend: 6 },
    { label: 'Prevented Breakdowns', value: 34, trend: 22 },
  ];

  return (
    <div className="space-y-5" data-testid="analytics-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Analytics</div>
        <h1 className="mt-1 text-3xl font-semibold text-slate-900 tracking-tight">Operational insights</h1>
        <p className="mt-1 text-sm text-slate-500">Performance, coverage, and revenue trends across the monitored fleet.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <Card key={k.label} className="p-4 border-slate-200 shadow-sm">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{k.label}</div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-mono text-2xl font-semibold text-slate-900">{k.value}</span>
              <span className={`text-xs font-medium ${k.trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {k.trend >= 0 ? '+' : ''}{k.trend}%
              </span>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Alerts by Type</div>
          <div className="mt-0.5 text-lg font-semibold text-slate-900">Most common diagnostic signals</div>
          <div className="h-72 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={alertsByType} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} width={170} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                <Bar dataKey="count" fill="#0052CC" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Devices by Health Band</div>
          <div className="mt-0.5 text-lg font-semibold text-slate-900">Fleet-wide distribution</div>
          <div className="h-72 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={healthPie} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                  {healthPie.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Average Response Time</div>
          <div className="mt-0.5 text-lg font-semibold text-slate-900">Trend over last 12 weeks</div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={responseTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="d" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                <YAxis fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                <Line type="monotone" dataKey="hours" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Alerts by Severity</div>
          <div className="mt-0.5 text-lg font-semibold text-slate-900">Current open alert mix</div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={alertsBySeverity}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                <YAxis fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {alertsBySeverity.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Seasonal System Stress</div>
          <div className="mt-0.5 text-lg font-semibold text-slate-900">Heating vs cooling load by month</div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={seasonalStress}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="m" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                <YAxis fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="heat" stackId="1" stroke="#EF4444" fill="#FEE2E2" />
                <Area type="monotone" dataKey="cool" stackId="1" stroke="#0052CC" fill="#DBEAFE" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Technician Workload</div>
          <div className="mt-0.5 text-lg font-semibold text-slate-900">Jobs handled per tech (active + monthly)</div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={workload}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                <YAxis fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                <Bar dataKey="jobs" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-5 border-slate-200 shadow-sm">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Monitoring-Driven Service Revenue</div>
        <div className="mt-0.5 text-lg font-semibold text-slate-900">Revenue from alert-to-service conversions</div>
        <div className="h-64 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monitoringRev}>
              <defs>
                <linearGradient id="revG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="m" fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
              <YAxis fontSize={11} stroke="#64748B" tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
              <Area type="monotone" dataKey="rev" stroke="#10B981" strokeWidth={2} fill="url(#revG)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
